import { useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SERVICES, MISSION_IMG, EXECUTIVES } from "@/data/services";
import { ArrowRight, Quote } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { Button } from "@/components/ui/button";
import { formatTZS } from "@/data/services";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.07 } }),
};

const VALUES = [
  ["100%", "Client focus"],
  ["6", "Service divisions"],
  ["2", "Global offices"],
  ["24h", "Response time"],
];

export default function Team() {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutItem, setCheckoutItem] = useState(null);
  const [bioMember, setBioMember] = useState(null);

  const orderService = (s) => { setCheckoutItem(s); setPickerOpen(false); setCheckoutOpen(true); };

  return (
    <div className="bg-brand-grey min-h-screen">
      <SEO
        title="Our Team | Tanz-Bridge International LTD"
        description="Meet the leadership and expert team behind Tanz-Bridge International LTD, delivering technical consultancy across Tanzania and beyond."
        path="/team"
      />
      <Navbar onOrder={() => setPickerOpen(true)} />

      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden grain bg-brand-dark">
        <div className="absolute inset-0 z-0">
          <img src={MISSION_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/90 to-brand-dark/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <motion.p initial="hidden" animate="show" variants={fadeUp}
            className="text-xs tracking-[0.3em] uppercase text-brand-yellow mb-5">
            Our People
          </motion.p>
          <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold tracking-tighter text-white max-w-3xl leading-tight">
            The management team behind our <span className="text-brand-yellow">commitment for excellence</span>.
          </motion.h1>
          <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="mt-6 text-lg text-gray-300 max-w-2xl">
            A multidisciplinary leadership team driving research, technology, trade and sustainable growth
            across Tanzania and the global market.
          </motion.p>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 -mt-12 relative z-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {VALUES.map(([n, l], i) => (
            <motion.div key={l} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              className="rounded-lg bg-brand-card border border-gray-200 shadow-sm p-6 text-center">
              <p className="text-3xl font-heading text-brand-blue">{n}</p>
              <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">{l}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* EXECUTIVE SPOTLIGHTS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-4 space-y-24" data-testid="exec-spotlights">
        {EXECUTIVES.map((ex, idx) => (
          <div key={ex.name} className="grid lg:grid-cols-5 gap-10 items-center" data-testid={`exec-spotlight-${idx}`}>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className={`lg:col-span-2 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-xl bg-brand-dark">
                <img src={ex.image} alt={ex.name} loading="eager" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-dark to-transparent p-6">
                  <span className="text-[10px] tracking-[0.2em] uppercase bg-brand-yellow text-brand-dark font-semibold px-2.5 py-1 rounded-sm">
                    {ex.badge}
                  </span>
                </div>
              </div>
            </motion.div>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} variants={fadeUp}
              className="lg:col-span-3">
              <p className="text-xs tracking-[0.2em] uppercase text-brand-blue mb-3">{ex.tagline}</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight text-brand-dark">{ex.name}</h2>
              <p className="text-brand-blue font-medium mt-1">{ex.role}</p>
              <p className="text-gray-600 leading-relaxed mt-6">{ex.bio}</p>
              <blockquote className="mt-6 relative rounded-lg bg-brand-card border border-gray-200 shadow-sm p-6 pl-14">
                <Quote size={26} className="absolute left-5 top-6 text-brand-yellow" />
                <p className="text-brand-dark italic leading-relaxed">{ex.quote}</p>
                <footer className="mt-4 text-sm text-gray-500">— {ex.name}, {ex.role}</footer>
              </blockquote>
            </motion.div>
          </div>
        ))}
      </section>

      {/* EXPERTISE GALLERY - service pictures */}
      <section className="bg-brand-dark py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-12 text-center">
            <p className="text-xs tracking-[0.2em] uppercase text-brand-yellow mb-3">Our expertise in action</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight text-white">
              Six divisions, one team.
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              return (
                <motion.div key={s.id} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                  data-testid={`team-service-${s.id}`}
                  className="group relative rounded-lg overflow-hidden h-48 border border-white/10">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/40 to-transparent" />
                  <div className="absolute bottom-4 left-5">
                    <h3 className="text-lg font-heading text-white">{s.name}</h3>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="rounded-2xl bg-brand-blue p-10 sm:p-16 text-center relative overflow-hidden">
          <h2 className="text-3xl sm:text-4xl font-heading font-extrabold text-white max-w-2xl mx-auto">
            Ready to work with our team?
          </h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto">
            Order a service today and pay securely with M-Pesa, or reach out for a free consultation.
          </p>
          <Button data-testid="team-cta-order" onClick={() => setPickerOpen(true)}
            className="mt-8 bg-brand-yellow hover:bg-brand-yellowhover text-brand-dark font-semibold rounded-sm px-8 py-6 text-base">
            Order a Service <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      <Footer />

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent data-testid="team-picker-dialog" className="bg-brand-card border border-gray-200 text-brand-dark max-w-lg">
          <DialogTitle className="sr-only">Choose a service to order</DialogTitle>
          <h3 className="text-2xl font-heading mb-1 text-brand-dark">Choose a service to order</h3>
          <p className="text-sm text-gray-500 mb-4">Pay securely with M-Pesa after selecting.</p>
          <div className="grid gap-3 max-h-[60vh] overflow-y-auto pr-1">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <button key={s.id} data-testid={`team-picker-${s.id}`} onClick={() => orderService(s)}
                  className="flex items-center gap-4 rounded-sm bg-brand-grey border border-gray-200 hover:border-brand-blue p-4 text-left transition-colors">
                  <div className="h-10 w-10 rounded-sm bg-brand-blue/10 flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-brand-blue" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-brand-dark">{s.name}</p>
                    <p className="text-xs text-gray-500">{s.tagline}</p>
                  </div>
                  <span className="text-brand-blue text-sm font-semibold">{formatTZS(s.price)}</span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} type="order" item={checkoutItem} />

      {/* Team member bio modal */}
      <Dialog open={!!bioMember} onOpenChange={(o) => !o && setBioMember(null)}>
        <DialogContent data-testid="team-bio-dialog" className="bg-brand-card border border-gray-200 text-brand-dark max-w-lg p-0 overflow-hidden gap-0">
          <DialogTitle className="sr-only">{bioMember?.name} biography</DialogTitle>
          {bioMember && (
            <div>
              <div className="flex items-center gap-4 p-6 bg-brand-dark">
                <img src={bioMember.image} alt={bioMember.name} className="h-16 w-16 rounded-lg object-cover object-top" />
                <div>
                  <h3 className="text-xl font-heading text-white">{bioMember.name}</h3>
                  <p className="text-sm text-brand-yellow">{bioMember.role}</p>
                </div>
              </div>
              <p className="p-6 text-gray-600 leading-relaxed text-sm">{bioMember.bio}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
